import { useEffect } from 'react';
import i18next from 'i18next';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://healthtravelturkiye.com';

export interface BreadcrumbItem {
  /**
   * i18n key from common.ts — will be translated at runtime.
   * Use this for static pages (e.g. 'breadcrumb_contact').
   */
  nameKey?: string;
  /**
   * Literal string — used for dynamic values like institution names
   * that are not in i18n files.
   */
  name?: string;
  url: string;
}

/**
 * Injects one or more Schema.org JSON-LD scripts into <head>.
 * Automatically prepends a BreadcrumbList based on `breadcrumbs`.
 * Cleans up on unmount.
 *
 * @param scriptId    Unique id for the <script> tag (prevents duplicates)
 * @param schemas     Array of Schema.org objects to inject alongside BreadcrumbList
 * @param breadcrumbs Array of { nameKey?, name?, url }
 *                    - nameKey: i18n common.ts key (translated automatically)
 *                    - name: literal string (used as-is, e.g. institution.name)
 *                    "Home" breadcrumb is always prepended automatically.
 * @param language    Current i18n language — pass i18n.language so the effect
 *                    re-runs and updates names on language change
 */
export function useSeoSchema(
  scriptId: string,
  schemas: object[],
  breadcrumbs: BreadcrumbItem[],
  language?: string,
) {
  useEffect(() => {
    const tc = i18next.getFixedT(language ?? i18next.language, 'common');

    // Always start with home
    const fullCrumbs: BreadcrumbItem[] = [
      { nameKey: 'breadcrumb_home', url: `${SITE_URL}/` },
      ...breadcrumbs,
    ];

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: fullCrumbs.map((crumb, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        // nameKey takes priority; fall back to literal name
        name: crumb.nameKey ? tc(crumb.nameKey) : (crumb.name ?? ''),
        item: crumb.url,
      })),
    };

    const allSchemas = [breadcrumbSchema, ...schemas];

    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(allSchemas);

    return () => {
      document.getElementById(scriptId)?.remove();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptId, language]);
}
